import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import {
    PolicyDialogComponent,
    TPolicyDialogData,
} from '@app/components/policy-dialog/policy-dialog.component';
import { UsersHttpService } from '@app/http/users-http.service';
import { IPendingPolicyDto, IPolicyConsentErrorDto } from '@app/interfaces/policy.interface';
import { AppService } from '@app/services/app.service';
import { IKuiDialogConfig, KuiDialogService } from '@kone/ui';
import {
    catchError,
    defaultIfEmpty,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
} from 'rxjs';

const CONSENT_DIALOG_CONFIG: IKuiDialogConfig<TPolicyDialogData> = {
    width: '700px',
    closable: false,
    dismissableMask: false,
    disableEscapeClose: true,
};

@Injectable({
    providedIn: 'root',
})
export class PolicyConsentService {
    private kuiDialogService = inject(KuiDialogService);
    // Resolved lazily: this service is created by the HTTP interceptor, AppService and
    // UsersHttpService depend on HttpClient.
    private injector = inject(Injector);

    private consent$: Observable<boolean> | null = null;
    private acceptanceCount = 0;

    /** Changes with every acceptance, so a request can tell whether consent was given while it was in flight. */
    get acceptances(): number {
        return this.acceptanceCount;
    }

    /** The policies the API still wants accepted, when the error is its 403 "consent required" response. */
    pendingPoliciesFrom(error: unknown): IPendingPolicyDto[] {
        if (!(error instanceof HttpErrorResponse) || error.status !== HttpStatusCode.Forbidden) {
            return [];
        }
        const pending = (error.error as Partial<IPolicyConsentErrorDto> | null)?.pending_policies;
        return Array.isArray(pending) ? pending : [];
    }

    /**
     * Opens the consent pop-up once, however many requests were rejected at the same time, and emits
     * whether the user accepted. Declining (or the pop-up being closed any other way) logs the user out.
     * `sentAt` is the `acceptances` value when the rejected request was sent.
     */
    requestConsent(policies: IPendingPolicyDto[], sentAt: number): Observable<boolean> {
        // Rejected before the user accepted, but the answer arrived afterwards: just replay it.
        if (!this.consent$ && sentAt < this.acceptanceCount) return of(true);

        this.consent$ ??= this.kuiDialogService
            .open<TPolicyDialogData>(PolicyDialogComponent, CONSENT_DIALOG_CONFIG, {
                mode: 'consent',
                policies,
            })
            .onClose.pipe(
                map(accepted => accepted === true),
                defaultIfEmpty(false),
                tap(accepted => {
                    if (accepted) this.acceptanceCount++;
                    else void this.injector.get(AppService).logout();
                }),
                finalize(() => (this.consent$ = null)),
                shareReplay(1),
            );
        return this.consent$;
    }

    /**
     * Asks for whatever is still pending before the user is handed off to another platform, and
     * emits whether they may continue.
     */
    ensureConsent(): Observable<boolean> {
        const sentAt = this.acceptanceCount;
        return this.injector
            .get(UsersHttpService)
            .getPendingPolicies()
            .pipe(
                // The API's own 403 still applies, so a failed check shouldn't block the user.
                catchError(() => of([])),
                switchMap(pending =>
                    pending.length ? this.requestConsent(pending, sentAt) : of(true),
                ),
            );
    }
}
